/* ==================================================================
   Tabela de preços — Reflect On Engenharia
   ------------------------------------------------------------------
   POLÍTICA: A TAXA DO CREA É REPASSE. QUANDO ELA SOBE, O PREÇO SOBE.

   Decidido em 29/08/2026, com o Welbert. A taxa do CREA não é margem:
   é dinheiro que o Alan recolhe do cliente e entrega ao conselho, como
   um tributo. Aumento de custo de fornecedor se repassa — e o site já
   descreve a coisa assim, "taxa do CREA, repasse ao conselho", não
   "nosso preço".

   Na prática: o SERVIÇO é a constante, e o total é a soma.

     total = servico + taxa do CREA + (memorial, se o cliente quiser)

   A alternativa considerada e descartada era ancorar no total anunciado
   e deixar a margem absorver o reajuste. Protegeria a copy, mas faria a
   margem do Alan encolher todo ano por decisão de terceiro, sem ninguém
   decidir nada. Entre proteger o número da página e proteger o ganho de
   quem assina o documento, protege-se o segundo — e se avisa a página.

   ------------------------------------------------------------------
   O QUE ISSO EXIGE, E POR QUE O ALARME ABAIXO EXISTE

   O reajuste da ART é anual e indexado ao INPC, e a decisão plenária do
   Confea sai até abril do ano ANTERIOR à vigência. Ou seja: dá pra ver
   chegando com meses de antecedência, não é sobressalto.

   Mas quando ele chega, o número anunciado muda — e esse número está
   escrito à mão em ~20 lugares de copy (app/page.tsx, lib/conteudo.ts,
   lib/servicos.ts, components/Footer.tsx) e ainda DESENHADO dentro do
   public/og.png. Nada disso o motor alcança sozinho, e parte nem deveria
   alcançar: "Por que R$ 249 custa menos que R$ 149" é retórica montada
   em cima do número, comparando com concorrente. Substituição mecânica
   ali produz frase quebrada.

   Então `TOTAIS_NA_COPY` guarda o que as páginas dizem HOJE, e
   `conferirTabela()` grita quando a conta deixa de bater. É o que
   transforma "alguém vai lembrar de atualizar o site" em erro visível
   no `npm run conferir`.
   ================================================================== */

/** Uma linha da tabela do CREA. No bloco 2 isto passa a vir do banco
 *  (`tabela_crea`), versionada por UF e vigência — o contrato promete
 *  cálculo pela "tabela vigente", e tabela vigente muda todo ano. */
export type LinhaCrea = {
  /** id da linha no banco. `null` enquanto a tabela for constante de código. */
  id: string | null;
  uf: string;
  /** teto da faixa em reais; `null` é a faixa superior, sem teto */
  faixaAte: number | null;
  valorTaxa: number;
  /** o ato que fixou o valor, para poder explicar o preço depois */
  fonte?: string;
};

export type Tabela = {
  /** o que a casa cobra pelo serviço na faixa de baixo — ESTA é a constante */
  servicoAte15k: number;
  /** idem, faixa de cima */
  servicoAcima15k: number;
  memorial: number;
  creaAte15k: LinhaCrea;
  creaAcima15k: LinhaCrea;
};

/** Onde a faixa do CREA vira. */
export const LIMITE_FAIXA = 15000;

/** Acima disso a obra é grande o bastante pra valer uma conferida, mesmo com
 *  escopo simples. Abaixo, valor alto só muda a faixa da taxa. */
export const LIMITE_PORTE = 60000;

/* ------------------------------------------------------------------
   ⚠️ TEMPORÁRIO — some no bloco 2, quando `tabela_crea` existir no banco.
   Enquanto isso é constante de código, e o `id: null` marca isso de
   propósito: pedido gravado com `tabelaCreaId: null` é pedido anterior à
   tabela versionada, e dá pra encontrar depois.

   Os valores de MG NÃO estão aqui, e não são inventados: entram quando o
   Alan confirmar a inscrição dele no CREA-MG. A estrutura já recebe.
   ------------------------------------------------------------------ */
export const TABELA_VIGENTE: Tabela = {
  servicoAte15k: 140.61,
  servicoAcima15k: 164.41,
  memorial: 150.0,
  creaAte15k: {
    id: null,
    uf: "SP",
    faixaAte: LIMITE_FAIXA,
    valorTaxa: 108.39,
    fonte: "Decisão Plenária Confea 0450/2025 (exercício 2026)",
  },
  creaAcima15k: {
    id: null,
    uf: "SP",
    faixaAte: null,
    valorTaxa: 285.59,
    fonte: "Decisão Plenária Confea 0450/2025 (exercício 2026)",
  },
};

/** O que a COPY das páginas anuncia hoje, sem memorial.
 *
 *  Não é fonte de preço — é uma fotografia do que está escrito à mão pelo
 *  site afora, para poder comparar. Ao atualizar a tabela do CREA, estes
 *  números vão divergir e `conferirTabela()` acusa; aí se decide o preço
 *  novo (provavelmente arredondado, porque R$ 270,61 não é preço), se
 *  atualiza a copy, e SÓ ENTÃO se mexe aqui. */
export const TOTAIS_NA_COPY = {
  ate15k: 249.0,
  acima15k: 450.0,
  memorial: 150.0,
} as const;

/** Arredonda pra centavo. Existe porque soma de ponto flutuante devolve
 *  coisas como 399.00000000000006, e centavo com treze casas decimais
 *  vazando pro banco e pro Stripe é dívida técnica barata de evitar agora. */
export function arredondar(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Reais → centavos inteiros. É o formato que o Stripe exige (`unit_amount`)
 *  e o único jeito honesto de somar dinheiro. Usado no bloco 4. */
export function emCentavos(reais: number): number {
  return Math.round(reais * 100);
}

/** A conta de uma faixa, aberta linha a linha.
 *
 *  É a ÚNICA aritmética de dinheiro do sistema. `classificar()` usa daqui, a
 *  calculadora da home usa daqui, e o servidor grava o que sai daqui. Ter a
 *  soma escrita em dois lugares é como o total do checkout passa a divergir
 *  do total anunciado na página. */
export function contaDaFaixa(
  acima: boolean,
  comMemorial: boolean,
  tabela: Tabela = TABELA_VIGENTE,
): { servico: number; taxa: number; memorial: number; total: number; crea: LinhaCrea } {
  const crea = acima ? tabela.creaAcima15k : tabela.creaAte15k;
  const servico = acima ? tabela.servicoAcima15k : tabela.servicoAte15k;
  const memorial = comMemorial ? tabela.memorial : 0;
  return {
    servico,
    taxa: crea.valorTaxa,
    memorial,
    total: arredondar(servico + crea.valorTaxa + memorial),
    crea,
  };
}

/**
 * Acusa quando a tabela deixou de bater com o que as páginas anunciam.
 *
 * Roda no `npm run conferir`, então uma atualização da taxa do CREA que
 * esqueça a copy quebra a conferência em vez de ir pro ar em silêncio —
 * que é exatamente o formato do defeito do checkout antigo do Alan: página
 * dizendo um número, cobrança fazendo outro.
 *
 * Devolve a lista de problemas em vez de lançar erro: quem chama decide o
 * que fazer com eles.
 */
export function conferirTabela(tabela: Tabela = TABELA_VIGENTE): string[] {
  const avisos: string[] = [];

  const casos = [
    ["até R$ 15 mil", contaDaFaixa(false, false, tabela).total, TOTAIS_NA_COPY.ate15k],
    ["acima de R$ 15 mil", contaDaFaixa(true, false, tabela).total, TOTAIS_NA_COPY.acima15k],
  ] as const;

  for (const [faixa, calculado, naCopy] of casos) {
    if (calculado !== naCopy) {
      avisos.push(
        `A faixa ${faixa} agora soma ${brl(calculado)}, mas as páginas anunciam ${brl(naCopy)}.\n` +
          `    Atualize a copy (app/page.tsx, lib/conteudo.ts, lib/servicos.ts, components/Footer.tsx),\n` +
          `    regere o public/og.png, e então ajuste TOTAIS_NA_COPY em lib/motor/tabela.ts.`,
      );
    }
  }

  if (tabela.memorial !== TOTAIS_NA_COPY.memorial) {
    avisos.push(
      `O memorial agora custa ${brl(tabela.memorial)}, mas as páginas anunciam ${brl(TOTAIS_NA_COPY.memorial)}.`,
    );
  }

  for (const [rotulo, servico] of [
    ["até R$ 15 mil", tabela.servicoAte15k],
    ["acima de R$ 15 mil", tabela.servicoAcima15k],
  ] as const) {
    if (servico <= 0) {
      avisos.push(`O serviço na faixa ${rotulo} está ${brl(servico)} — isso não é preço.`);
    }
  }

  return avisos;
}

/** "R$ 249" em vez de "R$ 249,00".
 *
 *  Para texto corrido — título, meta description, prompt do assistente —
 *  onde centavos zerados só fazem a frase tropeçar. Cai automaticamente no
 *  formato completo quando houver centavo de verdade, que é o caso em que
 *  omitir seria mentir sobre o preço. */
export function brlCurto(n: number): string {
  return Number.isInteger(n) ? "R$ " + n.toLocaleString("pt-BR") : brl(n);
}

/** Separador de milhar de verdade. A versão anterior escrevia "R$ 8000,00",
 *  que é exatamente o tipo de descuido de formatação de moeda que quebrou o
 *  checkout do site atual. */
export function brl(n: number): string {
  return (
    "R$ " +
    Number(n).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}
